/**
 * Block 7.6 (2026-05-27): FOP exclusion rescue UI.
 *
 * Показує acc.entries (70x cr_account, ФОП-org) для існуючого посоленого
 * документа. Bookkeeper може flag-нути transit-частину як
 * COMMISSIONER_TRANSIT (або інша з 7 reasons) щоб виключити з revenue
 * для FOP-єдинника.
 *
 * Disabled для non-70x entries (backend guard вже відмовить — UI лише
 * ховає dropdown). Server-side validation на ФОП-org теж є.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  commissionerWarning,
  FOP_EXCLUSION_REASON_LABELS,
  type FopIncomeExclusionReason,
} from '@/api/commissionerWarning';

interface Props {
  organizationId: string;
  documentId: string;
}

export default function FopExclusionRescueSection({ organizationId, documentId }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['acc-entries-rescue', organizationId, documentId],
    queryFn: () => commissionerWarning.listEntriesForDocument(organizationId, documentId),
    enabled: !!organizationId && !!documentId,
  });

  const [drafts, setDrafts] = useState<Record<string, { reason: FopIncomeExclusionReason; comment: string }>>({});

  const mut = useMutation({
    mutationFn: ({ entryId, reason, comment }: { entryId: string; reason: FopIncomeExclusionReason; comment: string }) =>
      commissionerWarning.patchFopExclusionReason(entryId, {
        fop_income_exclusion_reason: reason,
        comment: comment || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['acc-entries-rescue', organizationId, documentId] }),
  });

  if (q.isLoading) return null;
  const entries = q.data ?? [];
  const revenueEntries = entries.filter((e) => e.cr_account_code.startsWith('70'));
  if (revenueEntries.length === 0) return null;

  return (
    <div
      data-testid="fop-rescue-section"
      style={{
        marginTop: '1.25rem', padding: '0.75rem',
        background: 'var(--primary-soft)', border: '1px solid #bae6fd', borderRadius: 6,
      }}
    >
      <strong style={{ color: '#075985' }}>
        Розрахунок єдиного податку (ФОП) — виключення з доходу
      </strong>
      <p style={{ marginTop: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
        Для проводок з рахунками 70x можна позначити причину виключення з
        оподатковуваного доходу (ст.292.11 ПКУ). Доступно лише для ФОП-організацій;
        backend поверне 422 якщо org не ФОП.
      </p>
      <table style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#075985' }}>
            <th style={{ padding: '0.3rem' }}>#</th>
            <th style={{ padding: '0.3rem' }}>Дт</th>
            <th style={{ padding: '0.3rem' }}>Кт</th>
            <th style={{ padding: '0.3rem', textAlign: 'right' }}>Сума</th>
            <th style={{ padding: '0.3rem' }}>Поточна причина</th>
            <th style={{ padding: '0.3rem' }}>Нова причина</th>
            <th style={{ padding: '0.3rem' }}>Коментар</th>
            <th style={{ padding: '0.3rem' }}></th>
          </tr>
        </thead>
        <tbody>
          {revenueEntries.map((e, idx) => {
            const draft = drafts[e.id] ?? { reason: e.fop_income_exclusion_reason, comment: '' };
            return (
              <tr key={e.id} data-testid={`fop-rescue-row-${idx}`} style={{ borderTop: '1px solid #bae6fd' }}>
                <td style={{ padding: '0.3rem' }}>{e.line_number}</td>
                <td style={{ padding: '0.3rem' }}>{e.dt_account_code}</td>
                <td style={{ padding: '0.3rem' }}>{e.cr_account_code}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{Number(e.amount).toLocaleString('uk-UA')}</td>
                <td style={{ padding: '0.3rem', color: e.fop_income_exclusion_reason === 'NONE' ? '#475569' : '#075985' }}>
                  {FOP_EXCLUSION_REASON_LABELS[e.fop_income_exclusion_reason]}
                </td>
                <td style={{ padding: '0.3rem' }}>
                  <select
                    aria-label={`fop-reason-${e.id}`}
                    value={draft.reason}
                    onChange={(ev) =>
                      setDrafts((d) => ({
                        ...d,
                        [e.id]: { reason: ev.target.value as FopIncomeExclusionReason, comment: draft.comment },
                      }))
                    }
                  >
                    {Object.entries(FOP_EXCLUSION_REASON_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.3rem' }}>
                  <input
                    aria-label={`fop-comment-${e.id}`}
                    value={draft.comment}
                    placeholder="Підстава (audit)"
                    onChange={(ev) =>
                      setDrafts((d) => ({
                        ...d,
                        [e.id]: { reason: draft.reason, comment: ev.target.value },
                      }))
                    }
                  />
                </td>
                <td style={{ padding: '0.3rem' }}>
                  <button
                    disabled={draft.reason === e.fop_income_exclusion_reason || mut.isPending}
                    onClick={() =>
                      mut.mutate({ entryId: e.id, reason: draft.reason, comment: draft.comment })
                    }
                  >
                    Зберегти
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {mut.isError && (
        <div className="error" style={{ marginTop: '0.4rem' }}>
          Помилка збереження. Можливо: рахунок не 70x, або організація не ФОП.
        </div>
      )}
    </div>
  );
}
